/* eslint-disable no-nested-ternary */
import { useState } from 'react';
import { Tabs, Button } from 'react-daisyui';
import type {
  AccountDataSection,
  AccountData,
  User,
  KVPairs,
  ClientConfig,
  PlanData,
  DealData,
  EventLog,
} from '@types';
import { get } from '@/utils/queries';
import { Spinner } from '@/components/Spinner';
import UsersAccordionList from '@/components/UsersAccordionList/UsersAccordionList';
import { AdminUsersAccordionList } from '@/components/AdminUsersAccordionList/AdminUsersAccordionList';
import { openHubspotClient } from '@/utils/hubspot';
import { faHubspot } from '@fortawesome/free-brands-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { AccountConfig } from '@/components/AccountDetails/AccountConfig';
import { EmptyState } from '@/components/EmptyState';
import { IUserContext, useUserContext } from '@/context/UserContext';
import { SWRResponse } from 'swr';
import { Sort } from '@/types';
import { AccountDataDisplay } from './AccountDataDisplay';
import { AccountPlanDetails } from './AccountPlanDetails';
import { prettifyKey } from './utils';
import useAccountData from '../../hooks/useAccountData';
import UserEventLogs from '../UserEventLogs/UserEventLogs';
import useUsersList from '../../hooks/useUsersList';

const { Tab } = Tabs;

export function AccountDetailsLoader({
  clientId,
  tab = 'business_info',
  test = false,
  onTabChange,
}: {
  clientId: number;
  tab: AccountDataSection;
  test?: boolean;
  onTabChange: (AccountDataSection: string) => void;
}) {
  const { getAccountData } = useAccountData();
  const { getUsersListByClientId } = useUsersList();
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const { data: clientData, isValidating } = getAccountData(clientId);
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const { data: clientUsersData } = getUsersListByClientId(clientId);
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment
  const detailedData = clientData?.hubspot_data?.detailedData;

  const [sort, setSort] = useState<Sort>({ field: 'action', asc: true });
  const [searchText, setSearchText] = useState<string>('');
  const [selectedPage, setSelectedPage] = useState<number>(1);
  const [recordsPerPage, setRecordsPerPage] = useState<number>(5);

  const { data, isLoading } = get(
    `logs/${clientId}?page=${
      selectedPage - 1
    }&pageSize=${recordsPerPage}&q=${searchText}&sortField=${
      sort.field
    }&sortDirection=${sort.asc ? 'asc' : 'desc'}`
  ) as SWRResponse<{ data: EventLog[]; count: number }>;
  const logData = data?.data;
  const count = data?.count;

  if (isValidating) {
    return <Spinner className='mx-auto w-16 mt-10' />;
  }

  const currentClientData = {
    id: clientId,
    hubspot_id: (clientData as AccountData).hubspot_id,
    ...detailedData,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    config: clientData?.config ?? ({} as ClientConfig),
    users: clientUsersData as User[],
  } as AccountData;
  if (!currentClientData) {
    return <EmptyState name='Account Data' />;
  }

  return (
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    <AccountDetails
      clientData={currentClientData}
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      logData={logData || []}
      totalRecords={count as number}
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      clientUsersData={clientUsersData || []}
      test={test}
      tab={tab}
      onTabChange={onTabChange}
      sort={sort}
      searchText={searchText}
      selectedPage={selectedPage}
      recordsPerPage={recordsPerPage}
      setSort={setSort}
      setSelectedPage={setSelectedPage}
      setRecordsPerPage={setRecordsPerPage}
      setSearchText={setSearchText}
      isLoading={isLoading}
    />
  );
}

const TAB_ORDER = {
  business_info: 1,
  plan: 2,
  users: 3,
  config: 4,
  logs: 5,
};

type TabNamesTypes = 'business_info' | 'plan' | 'users' | 'config' | 'logs';

export function AccountDetails({
  clientData,
  clientUsersData,
  logData,
  tab = 'business_info',
  test = false,
  onTabChange,
  sort,
  searchText,
  selectedPage,
  recordsPerPage,
  setSort,
  setSelectedPage,
  setRecordsPerPage,
  setSearchText,
  totalRecords,
  isLoading,
}: {
  clientData: AccountData;
  clientUsersData: User;
  logData: EventLog[];
  tab: AccountDataSection;
  test?: boolean;
  onTabChange: (tabName: AccountDataSection) => void;
  sort: Sort;
  searchText: string;
  selectedPage: number;
  recordsPerPage: number;
  totalRecords: number;
  setSort: (sort: Sort) => void;
  setSelectedPage: (page: number) => void;
  setRecordsPerPage: (records: number) => void;
  setSearchText: (str: string) => void;
  isLoading: boolean;
}) {
  const { auth }: IUserContext = useUserContext();
  const size = 'sm';

  const tabTitles = ['business_info', 'plan', 'users', 'config'];

  if (auth?.isSuperAdmin || auth?.isAdmin) {
    tabTitles.push('logs');
  }

  const selectedTab: AccountDataSection = (
    tabTitles?.filter((t) => tab === t)?.length > 0 ? tab : tabTitles[0]
  ) as AccountDataSection;
  const [activeTab, setActiveTab] = useState<AccountDataSection>(selectedTab);
  return (
    <>
      <div className='flex justify-between items-center'>
        <Tabs
          variant='bordered'
          value={activeTab}
          onChange={(tabName) => {
            setActiveTab(tabName);
            onTabChange(tabName);
          }}
          data-testid='account-details-tabs'
        >
          {tabTitles
            .sort(
              (t1, t2) =>
                TAB_ORDER[t1 as TabNamesTypes] - TAB_ORDER[t2 as TabNamesTypes]
            )
            .map((title) => (
              <Tab
                key={title}
                value={title}
                data-testid={`tab_${title}`}
                className='capitalize'
              >
                {prettifyKey(title)}
              </Tab>
            ))}
        </Tabs>
      </div>

      {activeTab === 'logs' ? (
        logData !== undefined ? (
          <div className='p-4 max-h-[calc(100vh-180px)] overflow-y-auto'>
            <UserEventLogs
              logData={logData}
              sort={sort}
              searchText={searchText}
              selectedPage={selectedPage}
              recordsPerPage={recordsPerPage}
              setSort={setSort}
              setSelectedPage={setSelectedPage}
              setRecordsPerPage={setRecordsPerPage}
              setSearchText={setSearchText}
              totalRecords={totalRecords}
              isLoading={isLoading}
            />
          </div>
        ) : (
          <div className='p-4'> No logs Found </div>
        )
      ) : null}
      {activeTab === 'config' && clientData[activeTab] && (
        <div className='p-4 max-h-[calc(100vh-180px)] overflow-y-auto'>
          <AccountConfig
            config={clientData[activeTab] as unknown as ClientConfig[]}
            clientId={clientData.id}
          />
        </div>
      )}
      {activeTab === 'business_info' && !clientData[activeTab] && (
        <div className='p-4'> No Data Found </div>
      )}
      {activeTab === 'business_info' && clientData[activeTab] ? (
        <AccountDataDisplay
          companyData={clientData[activeTab] as KVPairs | KVPairs[]}
        />
      ) : null}
      {activeTab === 'plan' && !clientData[activeTab] && (
        <div className='p-4'> No Plan Available </div>
      )}
      {activeTab === 'plan' && clientData[activeTab] ? (
        <AccountPlanDetails
          plan={clientData[activeTab] as unknown as PlanData}
          size={size}
          showHubspotLink
          deals={clientData[activeTab].deals as unknown as DealData[]}
        />
      ) : null}
      {activeTab === 'users' && clientData[activeTab] ? (
        <div className='p-4 max-h-[calc(100vh-180px)] overflow-y-auto'>
          <UsersAccordionList
            clientData={clientData}
            users={clientData[activeTab] as unknown as User[]}
            size={size}
            showHubspotLink
            showDeleteButton
            test={test}
          />
          {clientUsersData && (
            <AdminUsersAccordionList
              users={clientUsersData as unknown as User[]}
              size={size}
            />
          )}
        </div>
      ) : null}
      {activeTab === 'plan' && clientData[activeTab] && (
        <div className='text-center'>
          <a
            href='https://meetings.hubspot.com/nick-nielson'
            target='_blank'
            className='btn btn-accent btn-sm mt-5 w-[200px]'
            rel='noreferrer'
          >
            Change plan
          </a>
        </div>
      )}
      {activeTab === 'business_info' && clientData[activeTab] && (
        <div className='text-center'>
          <Button
            color='warning'
            data-testid='hubspot-button'
            variant='outline'
            size={size}
            onClick={() => openHubspotClient(clientData?.hubspot_id)}
            startIcon={<FontAwesomeIcon icon={faHubspot as IconProp} />}
          />
        </div>
      )}
    </>
  );
}
